import request from '@/utils/request'

/**
 * 告警编码 V3 接口（/device/v3/alarmCode）
 *
 * 替代旧的 /dashboard/alarm/code 系列（见 @/api/production/warn.js 中的
 * codeList / addWarnCode / updateWarnCode / delWarnCodes，那几个仅供旧页面使用）。
 *
 * 字段说明（AlarmCode）：
 * - productModel 产品型号：A0运行类告警、A1工商业设备告警、A2eBlock100C告警（必填）
 * - type         设备类型：00默认、01PCS、02BCS、03BMS、04eLink、05水机、06消防、
 *                07 100C-dcdc、08 100C-MPPT、E1eBlock、E2EMS（必填）
 * - moduleType   模块类型：00默认；运营类告警 01需量、02逆流、03非计划、04离线、
 *                05电压、06温度、07收益、08效率、09soc（必填）
 * - code         故障编码（必填，整型）
 * - level        故障等级：0提示、1告警、2停机故障、3严重故障（必填）
 * - customerShow 是否给客户展示：0不展示、1展示
 *
 * 唯一约束：productModel + type + moduleType + code 四字段联合唯一。
 */

/**
 * 获取告警编码列表（分页）
 * 排序固定为 product_model, type, module_type, code 升序
 * @param {object} params
 * @param {number} [params.pageNum] 页码
 * @param {number} [params.pageSize] 每页数量
 * @param {string} [params.productModel] 产品型号（精确匹配）
 * @param {string} [params.type] 设备类型（精确匹配）
 * @param {string} [params.moduleType] 模块类型（精确匹配）
 * @param {number} [params.code] 故障编码（精确匹配）
 * @param {string} [params.alarmCode] 告警唯一编码（精确匹配）
 * @param {number} [params.customerShow] 是否给客户展示（精确匹配）
 * @param {number} [params.level] 故障等级（精确匹配）
 * @param {string} [params.description] 故障描述（模糊匹配）
 * @param {string} [params.details] 故障详情（模糊匹配）
 * @returns {import('axios').AxiosPromise}
 */
export function listAlarmCode(params) {
  return request({
    url: '/device/v3/alarmCode/list',
    method: 'GET',
    params
  })
}

/**
 * 获取告警编码详细信息
 * 注意：ID 不存在时返回 code=200 且 data=null，不返回 404
 * @param {number|string} id 主键ID
 * @returns {import('axios').AxiosPromise}
 */
export function getAlarmCode(id) {
  return request({
    url: `/device/v3/alarmCode/${id}`,
    method: 'GET'
  })
}

/**
 * 新增告警编码
 * id 不传，由数据库自增生成；四字段联合唯一冲突时返回 code=500
 * @param {object} data AlarmCode
 * @returns {import('axios').AxiosPromise}
 */
export function addAlarmCode(data) {
  return request({
    url: '/device/v3/alarmCode',
    method: 'POST',
    data
  })
}

/**
 * 修改告警编码
 *
 * 后端采用动态 SQL，仅更新非 null 字段，无法通过本接口把字段置为 null。
 * 且 productModel、type、moduleType、code、level 带有校验注解，
 * 即使不修改也必须携带原值，否则参数校验失败 —— 因此必须提交全量对象。
 *
 * @param {object} data AlarmCode（含 id，且必须为全量字段）
 * @returns {import('axios').AxiosPromise}
 */
export function updateAlarmCode(data) {
  return request({
    url: '/device/v3/alarmCode',
    method: 'PUT',
    data
  })
}

/**
 * 删除告警编码（物理删除，不可恢复）
 * @param {number|string|Array<number|string>} ids 主键ID，可传数组批量删除
 * @returns {import('axios').AxiosPromise}
 */
export function delAlarmCode(ids) {
  const idStr = Array.isArray(ids) ? ids.join(',') : ids
  return request({
    url: `/device/v3/alarmCode/${idStr}`,
    method: 'DELETE'
  })
}
